import { ACTION_REFERENCE_LOCATION } from '@publishing-engine/submission-service/src/fixtures/article-workflow';
import { CANONICAL_SUBMISSION_WF } from '@publishing-engine/submission-service/src/workflow-configuration/presets/canonical-workflow.transition-graphs';
import React from 'react';
import { AcceptForFormalAcceptanceModal } from './AcceptForFormalAcceptanceModal';
import { AuthorResponseUpdateModal } from './AuthorResponseUpdateModal';
import { AuthorCompleteChangeRequest } from './changeRequest/AuthorCompleteChangeRequest';
import { SubmitChangeRequest } from './changeRequest/SubmitChangeRequest';
import {
  createEditorialModal,
  IEditorialDecisionModal
} from './EditorialDecisionModalWrapper';
import { EditorialUnsubmitSubmissionModal } from './EditorialUnsubmitSubmissionModal';
import { ExtendSubmissionDeadlineModal } from './GrantSubmissionExtensionModal';
import { ProceedModal } from './ProceedModal';
import { ProvisionalAcceptanceModal } from './ProvisionalAcceptanceModal';
import { RequestAdjudicationAction } from './RequestAdjudicationAction';
import { RequestEarlyAssessmentAction } from './RequestEarlyAssessmentAction';
import { RequestImmediateRevisionModal } from './RequestImmediateRevisionModal';
import { RequestSubmissionDeadlineExtensionModal } from './RequestSubmissionDeadlineExtensionModal';
import { ResubmitAction } from './ResubmitAction';
import { EditorialRevertDecisionModal } from './RevertDecisionModal';
import { SubmitRevisionModal } from './SubmitRevisionModal';
import { AcceptTransferModal } from './transfer/AcceptTransferModal';
import { DeclineTransferModal } from './transfer/DeclineTransferModal';
import { OfferTransferModal } from './transfer/OfferTransferModal';
import { AuthorRequestSubmissionWithdrawal } from './withdraw/AuthorRequestSubmissionWithdrawal';
import { AuthorRevokeSubmissionWithdrawalRequest } from './withdraw/AuthorRevokeSubmissionWithdrawalRequest';
import { EditorialAcceptWithdrawalModal } from './withdraw/EditorialAcceptWithdrawalModal';
import { EditorialWithdrawSubmissionModal } from './withdraw/EditorialWithdrawSubmissionModal';
import { RequestRevisionModal } from './RequestRevisionModal';
import { RejectSubmissionModal } from './RejectSubmissionModal';
import { IAuthorICMJEFormList } from '@publishing-engine/domain/models';

// Get all state names
type StateNames = keyof (typeof CANONICAL_SUBMISSION_WF)['states'];
// Get all actions as a union
type AllActions =
  (typeof CANONICAL_SUBMISSION_WF)['states'][StateNames]['actions'][number];
// Only actions with roleType: 'editorial'

export interface IDecisionModalProps {
  onClose: () => void;
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  setActiveTab?: (tab: any) => void;
  isExtensionRequested?: boolean;
  proposedDate?: string;
}

export interface IDecisionModalPropsForSubmittingAuthor
  extends IDecisionModalProps {
  authorEmail?: string;
  authorStatus?: boolean;
  icmjeFormList?: IAuthorICMJEFormList[];
}

export type IDecisionModal = (props: IDecisionModalProps) => React.ReactNode;

export type DecisionMenuActions = {
  [K in keyof typeof ACTION_REFERENCE_LOCATION]: ReturnType<
    (typeof ACTION_REFERENCE_LOCATION)[K]
  > extends infer R
    ? R extends 'decisionMenu' | 'contextMenu'
      ? K
      : never
    : never;
}[keyof typeof ACTION_REFERENCE_LOCATION];
export type EditorialDecisionKey = Extract<
  AllActions,
  { roleType: 'editorial'; name: string }
>['name'];

type NonEditorialDecisionKey = Exclude<
  DecisionMenuActions,
  EditorialDecisionKey
>;

type IEditorialActionModal = (props: IDecisionModalProps) => React.ReactNode;

type EditorialDecisionToModal = {
  [K in EditorialDecisionKey]: IEditorialDecisionModal | IEditorialActionModal;
};
type NonEditorialDecisionToModal = {
  [K in NonEditorialDecisionKey]: IDecisionModal;
};
type DecisionToComponent = EditorialDecisionToModal &
  NonEditorialDecisionToModal;

const NotImplementedEditorialModal = createEditorialModal(() => {
  throw new Error('Modal not implemented for this editorial decision');
});

const decisionToComponent: DecisionToComponent = {
  // Below first group is decisions with Emails
  EditorialRequestImmediateRevision: RequestImmediateRevisionModal,
  EditorialOfferTransfer: OfferTransferModal,
  EditorialUnsubmitSubmission: EditorialUnsubmitSubmissionModal,
  EditorialRejectSubmission: RejectSubmissionModal,
  AcceptForFormalAcceptance: AcceptForFormalAcceptanceModal,
  ApproveSubmissionForPreProduction: ProvisionalAcceptanceModal,
  RequestRevision: RequestRevisionModal,
  EditorialWithdrawSubmission: EditorialWithdrawSubmissionModal,
  EditorialAcceptSubmissionWithdrawal: EditorialAcceptWithdrawalModal,

  'EditorialSubmissionDecision#approveForPeerReview': ProceedModal,
  AuthorRequestSubmissionWithdrawal: AuthorRequestSubmissionWithdrawal,
  AuthorRevokeSubmissionWithdrawalRequest:
    AuthorRevokeSubmissionWithdrawalRequest,
  ExtendSubmissionDeadline: ExtendSubmissionDeadlineModal,
  'SubmitRevision#fields': AuthorResponseUpdateModal,
  RequestSubmissionDeadlineExtension: RequestSubmissionDeadlineExtensionModal,
  DeclineTransfer: DeclineTransferModal,
  'AcceptTransferOffer#form': AcceptTransferModal,
  'EditorialSubmitChangeRequest#form': SubmitChangeRequest,
  'EditorialSubmitChangeRequest#fields': SubmitChangeRequest,
  'AuthorCompleteChangeRequest#form': AuthorCompleteChangeRequest,
  AuthorResubmitSubmission: ResubmitAction,
  'SubmitRevision#form': SubmitRevisionModal,
  // MISSING EditorialDecisionKey entries, using fallback modal
  // Or are ones which don't use a modal
  EditorialUploadCopyright: NotImplementedEditorialModal,
  EditorialUploadICMJE: NotImplementedEditorialModal,
  EditorialRequestCopyright: NotImplementedEditorialModal,
  RequestReview: NotImplementedEditorialModal,
  RequestReviewInRevision: NotImplementedEditorialModal,
  SetMinReviewCount: NotImplementedEditorialModal,
  RemoveEnqueuedReviewer: NotImplementedEditorialModal,
  EnqueueReviewRequest: NotImplementedEditorialModal,
  ToggleQueue: NotImplementedEditorialModal,
  EditSubmission: NotImplementedEditorialModal,
  CloneSubmission: NotImplementedEditorialModal,
  CreateEditorialFormData: NotImplementedEditorialModal,
  RequestEarlyAssessment: RequestEarlyAssessmentAction,
  RequestAdjudication: RequestAdjudicationAction,
  'AssignSubmission#editorial': NotImplementedEditorialModal,
  'AssignSubmission#owner': NotImplementedEditorialModal,
  'UnassignSubmission#editorial': NotImplementedEditorialModal,
  'UnassignSubmission#owner': NotImplementedEditorialModal,
  'SubmitLicensing#fields': NotImplementedEditorialModal,
  DenySubmissionDeadlineExtension: NotImplementedEditorialModal,
  ChangeArticleType: NotImplementedEditorialModal,
  RevertDecision: EditorialRevertDecisionModal,
  ResetAuthorCopyright: NotImplementedEditorialModal,
  AssignCOCEditor: NotImplementedEditorialModal,
  SubmitCOCRecommendation: NotImplementedEditorialModal,
  'RequestReview#automaticBoardMember': NotImplementedEditorialModal,
  'RequestReview#manualBoardMember': NotImplementedEditorialModal,
  COCAssignSubmission: NotImplementedEditorialModal,
  'RequestReviewInRevision#automaticBoardMember': NotImplementedEditorialModal,
  'RequestReviewInRevision#manualBoardMember': NotImplementedEditorialModal,
  UnassignCOCRole: NotImplementedEditorialModal,
  ModerateCOCRecommendation: NotImplementedEditorialModal
};

export const getModalForDecision = (decision: DecisionMenuActions) => {
  if (!decisionToComponent[decision]) {
    throw new Error(`No decision component found for decision: ${decision}`);
  }

  return decisionToComponent[decision];
};
